import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    BookOpen, Play, Clock, ChevronRight, Lock, CheckCircle
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useTrialStatus } from '../hooks/useTrialStatus'
import { supabase } from '../services/supabase'
import Button from '../components/Button'

interface Curso {
    id: string
    titulo: string
    descricao: string | null
    imagem_url: string | null
    duracao_estimada: number
    nivel: string
    status: string
    progresso: number
    total_aulas: number
    aulas_assistidas: number
}

export default function CursosDebug() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { subscriptionStatus, trialInfo } = useTrialStatus()
    const [cursos, setCursos] = useState<Curso[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [debugInfo, setDebugInfo] = useState<any>(null)

    useEffect(() => {
        if (user) {
            loadCursos()
            loadDebugInfo()
        }
    }, [user])

    const loadDebugInfo = async () => {
        try {
            // Verificar assinatura do usuário
            const { data: assinaturaData, error: assinaturaError } = await supabase
                .from('assinaturas')
                .select(`
                    *,
                    planos!inner(id, nome, status)
                `)
                .eq('user_id', user!.id)
                .eq('status', 'ativa')

            if (assinaturaError) {
                console.error('Erro ao carregar assinatura:', assinaturaError)
            }

            // Verificar associações cursos-planos
            const { data: associacoesData, error: associacoesError } = await supabase
                .from('cursos_planos')
                .select(`
                    *,
                    cursos!inner(id, titulo, status),
                    planos!inner(id, nome)
                `)

            if (associacoesError) {
                console.error('Erro ao carregar associações:', associacoesError)
            }

            setDebugInfo({
                user: user,
                subscriptionStatus,
                trialInfo,
                assinatura: assinaturaData,
                associacoes: associacoesData
            })

            console.log('=== DEBUG INFO ===')
            console.log('User:', user)
            console.log('Subscription Status:', subscriptionStatus)
            console.log('Trial Info:', trialInfo)
            console.log('Assinatura:', assinaturaData)
            console.log('Associações:', associacoesData)

        } catch (err) {
            console.error('Erro ao carregar debug info:', err)
        }
    }

    const loadCursos = async () => {
        try {
            setLoading(true)
            setError('')

            console.log('=== CARREGANDO CURSOS ===')
            console.log('User ID:', user!.id)

            // Primeiro, tentar buscar todos os cursos (sem RLS)
            const { data: todosCursos, error: todosError } = await supabase
                .from('cursos')
                .select('*')
                .eq('status', 'ativo')

            console.log('Todos os cursos (sem RLS):', todosCursos)
            console.log('Erro (se houver):', todosError)

            // Depois, buscar com RLS (como deveria funcionar)
            const { data: cursosData, error: cursosError } = await supabase
                .from('cursos')
                .select(`
                    id,
                    titulo,
                    descricao,
                    imagem_url,
                    duracao_estimada,
                    nivel,
                    status
                `)
                .eq('status', 'ativo')
                .order('ordem_exibicao')

            console.log('Cursos com RLS:', cursosData)
            console.log('Erro RLS:', cursosError)

            if (cursosError) {
                console.error('Erro ao carregar cursos:', cursosError)
                setError(`Erro ao carregar cursos: ${cursosError.message}`)
                return
            }

            // Para cada curso, calcular progresso e total de aulas
            const cursosComProgresso = await Promise.all(
                (cursosData || []).map(async (curso) => {
                    // Calcular progresso usando a função SQL
                    const { data: progressoData, error: progressoError } = await supabase
                        .rpc('calcular_progresso_curso', {
                            p_user_id: user!.id,
                            p_curso_id: curso.id
                        })

                    if (progressoError) {
                        console.error('Erro ao calcular progresso:', progressoError)
                    }

                    // Buscar módulos do curso
                    const { data: modulos } = await supabase
                        .from('modulos')
                        .select('id')
                        .eq('curso_id', curso.id)

                    const moduloIds = modulos?.map(m => m.id) || []

                    // Contar total de aulas
                    const { count: totalAulas } = await supabase
                        .from('aulas')
                        .select('*', { count: 'exact', head: true })
                        .in('modulo_id', moduloIds)

                    // Buscar aulas dos módulos
                    const { data: aulas } = await supabase
                        .from('aulas')
                        .select('id')
                        .in('modulo_id', moduloIds)

                    const aulaIds = aulas?.map(a => a.id) || []

                    // Contar aulas assistidas
                    const { count: aulasAssistidas } = await supabase
                        .from('progresso_aulas')
                        .select('*', { count: 'exact', head: true })
                        .eq('user_id', user!.id)
                        .eq('assistida', true)
                        .in('aula_id', aulaIds)

                    return {
                        ...curso,
                        progresso: progressoData || 0,
                        total_aulas: totalAulas || 0,
                        aulas_assistidas: aulasAssistidas || 0
                    }
                })
            )

            console.log('Cursos com progresso:', cursosComProgresso)
            setCursos(cursosComProgresso)

        } catch (err) {
            console.error('Erro ao carregar cursos:', err)
            setError('Erro ao carregar cursos. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const verificarAcesso = () => {
        console.log('=== VERIFICANDO ACESSO ===')
        console.log('Subscription Status:', subscriptionStatus)
        console.log('Trial Info:', trialInfo)

        // Verificar se o usuário tem plano Medium ou Pro
        if (subscriptionStatus === 'sem_assinatura') {
            console.log('Sem assinatura')
            return false
        }

        // Se está no trial, verificar se tem acesso aos cursos
        if (subscriptionStatus === 'trial_ativo' && trialInfo?.plano_nome) {
            const temAcesso = ['medium', 'pro'].includes(trialInfo.plano_nome.toLowerCase())
            console.log('Trial ativo, plano:', trialInfo.plano_nome, 'Tem acesso:', temAcesso)
            return temAcesso
        }

        // Se tem assinatura ativa, verificar plano
        if (subscriptionStatus === 'assinatura_ativa' && trialInfo?.plano_nome) {
            const temAcesso = ['medium', 'pro'].includes(trialInfo.plano_nome.toLowerCase())
            console.log('Assinatura ativa, plano:', trialInfo.plano_nome, 'Tem acesso:', temAcesso)
            return temAcesso
        }

        console.log('Status não reconhecido')
        return false
    }

    const formatarDuracao = (minutos: number) => {
        const horas = Math.floor(minutos / 60)
        const mins = minutos % 60
        if (horas > 0) {
            return `${horas}h ${mins}min`
        }
        return `${mins}min`
    }

    const getNivelColor = (nivel: string) => {
        switch (nivel) {
            case 'iniciante':
                return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
            case 'intermediario':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
            case 'avancado':
                return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
        }
    }

    const temAcesso = verificarAcesso()

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Debug Panel */}
                {debugInfo && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                            🔍 Debug Info
                        </h3>
                        <div className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
                            <p><strong>User ID:</strong> {debugInfo.user?.id}</p>
                            <p><strong>Subscription Status:</strong> {debugInfo.subscriptionStatus}</p>
                            <p><strong>Plano:</strong> {debugInfo.trialInfo?.plano_nome || 'N/A'}</p>
                            <p><strong>Tem Acesso:</strong> {temAcesso ? '✅ Sim' : '❌ Não'}</p>
                            <p><strong>Assinatura:</strong> {debugInfo.assinatura?.length || 0} registros</p>
                            <p><strong>Associações:</strong> {debugInfo.associacoes?.length || 0} registros</p>
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        Cursos Disponíveis (Debug)
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Aprenda técnicas avançadas para impulsionar suas vendas
                    </p>
                </div>

                {/* Error */}
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                        <p className="text-red-800 dark:text-red-200">{error}</p>
                    </div>
                )}

                {/* Acesso Restrito */}
                {!temAcesso && (
                    <div className="text-center py-16">
                        <Lock className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            Acesso Restrito
                        </h1>
                        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
                            Os cursos estão disponíveis apenas para usuários dos planos <strong>Medium</strong> e <strong>Pro</strong>.
                            Faça upgrade do seu plano para ter acesso a todo o conteúdo educacional.
                        </p>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                            <p><strong>Status atual:</strong> {subscriptionStatus}</p>
                            <p><strong>Plano:</strong> {trialInfo?.plano_nome || 'N/A'}</p>
                        </div>
                        <Button
                            onClick={() => navigate('/planos')}
                            variant="btn_add"
                            className="px-8 py-3"
                        >
                            Ver Planos Disponíveis
                        </Button>
                    </div>
                )}

                {/* Grid de Cursos */}
                {temAcesso && cursos.length === 0 && !loading && (
                    <div className="text-center py-16">
                        <BookOpen className="mx-auto h-24 w-24 text-gray-400 dark:text-gray-500 mb-6" />
                        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Nenhum curso disponível
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                            Novos cursos serão adicionados em breve.
                        </p>
                    </div>
                )}

                {/* Grid de Cursos */}
                {temAcesso && cursos.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {cursos.map((curso) => (
                            <div
                                key={curso.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                                onClick={() => navigate(`/cursos/${curso.id}`)}
                            >
                                {/* Imagem do Curso */}
                                <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 relative">
                                    {curso.imagem_url ? (
                                        <img
                                            src={curso.imagem_url}
                                            alt={curso.titulo}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full">
                                            <BookOpen className="h-16 w-16 text-white opacity-80" />
                                        </div>
                                    )}

                                    {/* Badge de Nível */}
                                    <div className="absolute top-3 left-3">
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getNivelColor(curso.nivel)}`}>
                                            {curso.nivel}
                                        </span>
                                    </div>

                                    {/* Badge de Progresso */}
                                    {curso.progresso > 0 && (
                                        <div className="absolute top-3 right-3">
                                            <div className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                                <CheckCircle className="h-3 w-3" />
                                                {curso.progresso}%
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Conteúdo do Card */}
                                <div className="p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2">
                                        {curso.titulo}
                                    </h3>

                                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                                        {curso.descricao || 'Descrição não disponível'}
                                    </p>

                                    {/* Informações do Curso */}
                                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
                                        <div className="flex items-center gap-1">
                                            <Clock className="h-4 w-4" />
                                            <span>{formatarDuracao(curso.duracao_estimada)}</span>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Play className="h-4 w-4" />
                                            <span>{curso.total_aulas} aulas</span>
                                        </div>
                                    </div>

                                    {/* Barra de Progresso */}
                                    {curso.progresso > 0 && (
                                        <div className="mb-4">
                                            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                <span>Progresso</span>
                                                <span>{curso.aulas_assistidas}/{curso.total_aulas} aulas</span>
                                            </div>
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                    style={{ width: `${curso.progresso}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Botão de Ação */}
                                    <div className="flex items-center justify-between">
                                        <Button
                                            variant={curso.progresso > 0 ? 'btn_secondary' : 'btn_add'}
                                            onClick={() => {
                                                navigate(`/cursos/${curso.id}`)
                                            }}
                                            className="flex items-center gap-2"
                                        >
                                            {curso.progresso > 0 ? 'Continuar' : 'Começar'}
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
